// "Mark handled" UI pieces for the Leads view (AFTERNOON-2026-10-01 B4).
// The logic is in ./lib/leadHandled; these are only its controls, kept out of the
// 3000-line App.jsx so a test can render them with react-dom alone (this repo has
// no @testing-library).
import React, { useState } from 'react';
import {
  NOTE_MAX, MAX_BULK, normalizeNote, noteLength, parseLeadIds, planHandled, bulkConfirmText,
  isHandled, handledAt,
} from './lib/leadHandled';

const btn = {
  borderRadius: 7, padding: '6px 11px', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
};
const input = {
  fontSize: 13, padding: '6px 9px', border: '1px solid #cbd5e1', borderRadius: 7, minWidth: 260,
};

function fmt(d) {
  try {
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_) { return String(d); }
}

/** The Handled cell: a pill with the time, the note on hover and beside it. */
export function HandledCell({ lead }) {
  if (!isHandled(lead)) return <span data-testid="handled-cell">—</span>;
  const at = handledAt(lead);
  const note = lead.processed_note || '';
  return (
    <span data-testid="handled-cell" title={note ? `Note: ${note}` : 'Marked handled'}>
      <span className="pill" style={{ background: '#dcfce7', color: '#166534' }}>
        handled{at ? ` · ${fmt(at)}` : ''}
      </span>
      {note && (
        <span style={{ marginLeft: 6, fontSize: 12, color: '#475569' }}>
          {note.length > 40 ? note.slice(0, 40) + '…' : note}
        </span>
      )}
    </span>
  );
}

/** Inline form under a row: one-line note (when the column exists) + confirm. */
export function HandledRowForm({ lead, noteEnabled, busy, onConfirm, onCancel }) {
  const [note, setNote] = useState('');
  const submit = (e) => { e.preventDefault(); if (!busy) onConfirm(lead.id, normalizeNote(note)); };
  return (
    <form onSubmit={submit} data-testid="handled-row-form"
      style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 0' }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Mark handled</span>
      {noteEnabled ? (
        <input
          data-testid="handled-note"
          style={input} value={note} maxLength={NOTE_MAX} autoFocus
          placeholder={`Optional one-line note (max ${NOTE_MAX})`}
          onChange={(e) => setNote(e.target.value)}
        />
      ) : null}
      {noteEnabled ? (
        <span data-testid="handled-note-count" style={{ fontSize: 12, color: noteLength(note) >= NOTE_MAX ? '#b91c1c' : '#475569' }}>
          {noteLength(note)}/{NOTE_MAX}
        </span>
      ) : (
        <span style={{ fontSize: 12, color: '#475569' }}
          title="processed_note does not exist yet (migrations/2026-10-01-leads-processed-note.sql)">
          (notes available after the migration)
        </span>
      )}
      <button type="submit" disabled={busy} data-testid="handled-confirm"
        style={{ ...btn, background: '#166534', color: 'white', opacity: busy ? 0.5 : 1 }}>
        Mark handled
      </button>
      <button type="button" onClick={onCancel} style={{ ...btn, background: '#f1f5f9', color: '#475569' }}>
        Cancel
      </button>
      <span style={{ fontSize: 12, color: '#475569' }}>Nothing is sent. The lead drops off the morning brief.</span>
    </form>
  );
}

/**
 * Bulk: rows ticked in the table plus ids pasted from the brief's call list, one
 * note, one confirm. Calls onMark(ids, note) with ONLY the ids whose state changes.
 */
export function HandledBulkBar({ leads, selectedIds, onSelectedChange, noteEnabled, busy, onMark, confirmFn }) {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [note, setNote] = useState('');
  const plan = planHandled(leads, selectedIds, true);
  const ask = confirmFn || ((msg) => window.confirm(msg));

  const addPasted = () => {
    const ids = parseLeadIds(pasteText);
    onSelectedChange([...new Set([...(selectedIds || []), ...ids])]);
    setPasteText('');
    setPasteOpen(false);
  };

  const mark = async () => {
    if (busy || plan.change.length === 0) return;
    if (!ask(bulkConfirmText(plan.change.length, note))) return;
    const res = await onMark(plan.change, normalizeNote(note));
    if (res && res.ok) { setNote(''); onSelectedChange([]); }
  };

  const over = plan.change.length > MAX_BULK;
  return (
    <div className="card" data-testid="handled-bulk-bar"
      style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span data-testid="handled-bulk-summary" style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>
          {(selectedIds || []).length} selected
          {(selectedIds || []).length > 0 && (
            <span style={{ fontWeight: 400, color: '#64748b' }}>
              {' '}· {plan.change.length} to mark
              {plan.already.length ? ` · ${plan.already.length} already handled` : ''}
              {plan.unknown.length ? ` · ${plan.unknown.length} not in the loaded leads (skipped)` : ''}
            </span>
          )}
        </span>
        <button type="button" data-testid="handled-paste-open" onClick={() => setPasteOpen((v) => !v)}
          style={{ ...btn, background: '#f1f5f9', color: '#4338ca' }}>
          Paste call-list ids
        </button>
        {(selectedIds || []).length > 0 && (
          <>
            {noteEnabled && (
              <input data-testid="handled-bulk-note" style={input} value={note} maxLength={NOTE_MAX}
                placeholder={`One note for the batch (optional, max ${NOTE_MAX})`}
                onChange={(e) => setNote(e.target.value)} />
            )}
            {noteEnabled && (
              <span data-testid="handled-bulk-note-count" style={{ fontSize: 12, color: noteLength(note) >= NOTE_MAX ? '#b91c1c' : '#475569' }}>
                {noteLength(note)}/{NOTE_MAX}
              </span>
            )}
            <button type="button" data-testid="handled-bulk-mark" onClick={mark}
              disabled={busy || plan.change.length === 0 || over}
              title={over ? `At most ${MAX_BULK} at a time` : ''}
              style={{ ...btn, background: '#166534', color: 'white',
                       opacity: busy || plan.change.length === 0 || over ? 0.5 : 1 }}>
              Mark {plan.change.length} handled
            </button>
            <button type="button" data-testid="handled-bulk-clear" onClick={() => onSelectedChange([])}
              style={{ ...btn, background: '#f1f5f9', color: '#475569' }}>
              Clear selection
            </button>
          </>
        )}
      </div>
      {pasteOpen && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <textarea data-testid="handled-paste-text" rows={4} value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder="Paste lines from the morning brief's call list. Every lead id in the text is picked up; everything else is ignored."
            style={{ ...input, minWidth: 420, flex: 1, fontFamily: 'monospace', fontSize: 12 }} />
          <button type="button" data-testid="handled-paste-add" onClick={addPasted}
            style={{ ...btn, background: '#1d4ed8', color: 'white' }}>
            Select {parseLeadIds(pasteText).length} id(s)
          </button>
        </div>
      )}
    </div>
  );
}

/** Shown right after a mark: says what happened and offers Undo for exactly that batch. */
export function HandledUndoBanner({ action, busy, onUndo, onDismiss }) {
  if (!action || !action.ids || action.ids.length === 0) return null;
  return (
    <div data-testid="handled-undo-banner" className="card"
      style={{ padding: '10px 14px', marginBottom: 12, background: '#f0fdf4', display: 'flex',
               gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, color: '#166534' }}>{action.message}</span>
      <button type="button" data-testid="handled-undo" disabled={busy} onClick={() => onUndo(action.ids)}
        style={{ ...btn, background: '#166534', color: 'white', opacity: busy ? 0.5 : 1 }}>
        Undo ({action.ids.length})
      </button>
      <button type="button" onClick={onDismiss} style={{ ...btn, background: 'transparent', color: '#475569' }}>
        Dismiss
      </button>
    </div>
  );
}
